import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import utils from '../../utils';
import { fetchNotifications, addUpdateNotifications, notifyBonusUsers } from '../../database/databaseNotifications.js';

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    message: '',
    datetime: '',
  });

  useEffect(() => {
    (async () => {
      const data = await fetchNotifications();
      setNotifications(data || []);
    })();
  }, []);

  const openAddModal = () => {
    setEditingId(null);
    setFormData({ message: '', datetime: utils.toInputDateTime(new Date()) });
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditingId(row.id);
    setFormData({
      message: row.message || '',
      datetime: row.datetime ? utils.toInputDateTime(new Date(row.datetime)) : utils.toInputDateTime(new Date()),
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const { message, datetime } = formData;
    if (!message.trim()) return alert('El mensaje es obligatorio.');
    if (!datetime) return alert('La fecha y hora es obligatoria.');

    const notification = { message, datetime: utils.inputToISO(datetime) };
    const success = await addUpdateNotifications(notification, editingId);
    if (success) {
      notifyBonusUsers(message);
      await fetchNotifications();
      setShowModal(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta notificación definitivamente?')) return;
    const { error } = await supabase.from('notifications').delete().eq('id', id);
    if (error) alert('Error deleting: ' + error.message);
    else fetchNotifications();
  };

  return (
    <section className="w-[95%] md:w-[90%] lg:w-[80%] mx-auto py-6">
      {/* Header */}
      <div className="flex flex-wrap gap-3 items-center justify-end mb-4">
        <button onClick={openAddModal} className="w-full sm:w-auto bg-terracotta text-cream px-4 py-2 rounded hover:bg-terracotta-dark transition">
          Nueva Notificación
        </button>
      </div>

      {/* Desktop table (>= md) */}
      <div className="hidden md:block overflow-x-auto rounded border border-sand">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            {/* Percent-based column widths */}
            <col style={{ width: '55%' }} />
            <col style={{ width: '30%' }} />
            <col style={{ width: '15%' }} />
          </colgroup>
          <thead>
            <tr className="bg-sand text-left text-terracotta-dark">
              <th className="px-[2.5%] py-3 border">Mensaje</th>
              <th className="px-[2.5%] py-3 border">Fecha y Hora</th>
              <th className="px-[2.5%] py-3 text-center border">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {notifications.map((n) => (
              <tr key={n.id} className="hover:bg-sand/40">
                <td className="px-[2.5%] py-3 border align-top break-words">{n.message}</td>
                <td className="px-[2.5%] py-3 border align-top whitespace-nowrap">{n.datetime ? new Date(n.datetime).toLocaleString() : '—'}</td>
                <td className="px-[2.5%] py-3 border text-center">
                  <div className="flex items-center justify-center gap-3">
                    <button onClick={() => openEditModal(n)} className="text-blue-600 hover:underline">
                      Editar
                    </button>
                    <button onClick={() => handleDelete(n.id)} className="text-red-600 hover:underline">
                      Borrar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {notifications.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center py-6">
                  No hay registros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile / Small screens: Card list (< md) */}
      <div className="md:hidden space-y-3">
        {notifications.length === 0 && <div className="text-center py-4 border border-sand rounded">No hay registros.</div>}

        {notifications.map((n) => (
          <div key={n.id} className="border border-sand rounded-lg p-4 shadow-sm bg-card">
            <div className="flex flex-col gap-2">
              {/* Row: Message */}
              <div className="flex gap-2">
                <span className="w-[35%] text-sm font-medium text-ink/70">Mensaje</span>
                <span className="w-[65%] break-words">{n.message}</span>
              </div>
              {/* Row: Datetime */}
              <div className="flex gap-2">
                <span className="w-[35%] text-sm font-medium text-ink/70">Fecha y hora</span>
                <span className="w-[65%]">{n.datetime ? new Date(n.datetime).toLocaleString() : '—'}</span>
              </div>
            </div>

            <div className="mt-4 flex justify-between">
              <button onClick={() => openEditModal(n)} className="px-3 py-2 rounded border w-[48%] text-blue-700 border-blue-200">
                Editar
              </button>
              <button onClick={() => handleDelete(n.id)} className="px-3 py-2 rounded border w-[48%] text-red-700 border-red-200">
                Borrar
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-card p-6 rounded-lg shadow-lg w-[96%] sm:w-[85%] md:w-[70%] lg:w-[50%] xl:w-[40%] max-w-[900px]">
            <h3 className="text-lg sm:text-xl font-serif text-terracotta mb-4">{editingId ? 'Editar notificación' : 'Agregar nueva notificación'}</h3>

            <form onSubmit={handleSave} className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium">Mensaje</span>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData((f) => ({ ...f, message: e.target.value }))}
                  className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded min-h-[120px]"
                  required
                />
              </label>

              <label className="block">
                <span className="text-sm font-medium">Fecha y hora</span>
                <input
                  type="datetime-local"
                  value={formData.datetime}
                  onChange={(e) => setFormData((f) => ({ ...f, datetime: e.target.value }))}
                  className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                  required
                />
              </label>

              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded border border-sand text-ink hover:bg-sand/40 w-[45%] sm:w-auto">
                  Cancelar
                </button>
                <button type="submit" className="bg-terracotta text-cream px-4 py-2 rounded hover:bg-terracotta-dark transition w-[45%] sm:w-auto">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
